import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { useBeforeunload } from '@dni-react-hooks/use-beforeunload'
import { Alert } from '@pets-ui/Alert/Alert'
import { ArrowLeftSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { format } from 'date-fns'
import queryString from 'query-string'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOtpSendErrorHandler } from '../../../hooks/use-otp-send-error-handler'
import { useEffectOnce } from '../../../hooks/useEffectOnce'
import { useOtpCosignSend } from '../../../hooks/useOtpCosignSend'
import { useTimer } from '../../../hooks/useTimer'
import { LoadingProvider } from '../../LoadingProvider'
import { useContextCosign } from '../../useContextCosign'
import { CardTitleWithIcon } from '../login/CardTitleWithIcon'
import { InputVerification } from '../login/InputVerification'
import { BindStep } from '../login/types'
import { LoginLink } from '../LoginLink'

const styles = stylex.create({
  subTitleContainer: {
    gap: '4px',
    alignItems: 'center',
    color: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginBottom: '20px',
    marginTop: '20px',
  },
  validateCodeContainer: {
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  resendContainer: {
    gap: '4px',
    display: 'flex',
  },
  validateCodeTitle: {
    color: '#4B4B4B',
  },
  validateResendText: {
    color: '#AAAAAA',
  },
})

interface BindPhoneProps {
  email: string
  setStep: (step: BindStep) => void
}

export function BindEmailValidation(props: BindPhoneProps) {
  const { t } = useTranslation()
  const { setStep, email } = props
  const { tokenLoginUrl, redirectUrl, fallbackUrl } = useContextCosign()
  const { bindSendOtp, isMutating } = useOtpCosignSend({ tokenLoginUrl, redirectUrl, fallbackUrl })
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const { startTimer, seconds, isCounting } = useTimer()
  const [remainingTimes, setRemainingTimes] = useState<number>()
  const [inputVerificationError, setInputVerificationError] = useState('')
  const search = queryString.parse(window.location.search)
  const canSendOtp = useRef(!search?.otpCode)

  const handleOtpSendError = useOtpSendErrorHandler(useCallback((detail) => {
    startTimer(detail.expiredAt)
    setRemainingTimes(detail.remainingTimes ?? 0)
    sendEvent({ eventName: 'mobile OTP resend limit reached', eventParams: { event_category: 'Mobile OTP' } })
  }, [startTimer, sendEvent]))

  const handleSendOtpSms = useCallback(async () => {
    try {
      canSendOtp.current = false
      const { data } = await bindSendOtp('email')
      startTimer(data.expiredAt)
      setRemainingTimes(data.remainingTimes)
      if (data.remainingTimes < 5) sendEvent({ eventName: 'Email OTP resend', eventParams: { event_category: 'Email OTP', email_otp_resend_clicks: 5 - data.remainingTimes } })
    }
    catch (error) {
      handleOtpSendError(error)
    }
  }, [bindSendOtp, sendEvent, startTimer, handleOtpSendError])

  useEffect(() => {
    if (!canSendOtp.current) return
    handleSendOtpSms()
  }, [handleSendOtpSms])

  const handleResendOtp = useCallback(() => {
    handleSendOtpSms()
    setInputVerificationError('')
  }, [handleSendOtpSms])

  const handleClickBack = () => {
    setStep(BindStep.INPUT_ACCOUNT)
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  }

  useBeforeunload(() => {
    sendEvent({ eventName: 'Email OTP drop', eventParams: { event_category: 'Email OTP' } })
  })

  useEffectOnce(() => {
    sendEvent({ eventName: 'Email OTP started', eventParams: { event_category: 'Email OTP' } })
  })

  const shouldShowRemainingTimesAlert = remainingTimes != null && seconds !== 0 && (remainingTimes >= 0 && remainingTimes < 5)
  const shouldShowResendText = remainingTimes !== 0 || seconds === 0
  return (
    <LoadingProvider loading={isMutating}>
      <CardTitleWithIcon
        title={t('login.verifyIdentity')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleClickBack}
      />
      <div {...stylex.props(styles.subTitleContainer)}>
        <Text variant="body">{t('login.sendVerifyCodeTo')}</Text>
        <Text variant="h5">{email}</Text>
      </div>
      <div {...stylex.props(styles.validateCodeContainer)}>
        <Text variant="caption" rootStyle={styles.validateCodeTitle}>{t('login.sendEmailTitle')}</Text>
        <InputVerification error={inputVerificationError} setError={setInputVerificationError} purpose="bind" />
        {shouldShowResendText && (
          <div {...stylex.props(styles.resendContainer)}>
            <Text variant="caption" rootStyle={styles.validateResendText}>
              {t('login.didntReceiveVerificationCode')}
              {seconds > 0 ? `(${seconds}s)` : ''}
            </Text>
            <LoginLink disabled={isCounting} onClick={handleResendOtp}>{t('login.resend')}</LoginLink>
          </div>
        )}
        {shouldShowRemainingTimesAlert
        && (
          <Alert type="warning">
            <Text variant="caption">
              {t('login.resendErmainingTimesContent', { remainingTimes })}
              {remainingTimes === 0 && `${t('login.resendWaitContent')}(${format(seconds * 1000, 'mm:ss')})`}
            </Text>
          </Alert>
        )}
      </div>
    </LoadingProvider>
  )
}
